// Catálogo de novedades del ERP.
//
// NORMA: cada utilidad nueva del ERP se entrega con una mini guía de 1 o 2
// tarjetas que avisa de que existe y enseña cómo se usa. Cada persona la ve
// una sola vez (se guarda en el servidor, preferencia `novedades_vistas`, y
// sigue a la persona en todos sus dispositivos). La lanza
// NovedadesPendientes.js (montado una vez en App.js) con el motor de la guía
// interactiva (GuiaInteractiva.js), cuando la pantalla en la que está la
// persona tiene a la vista lo que se quiere enseñar.
//
// Cómo añadir una novedad:
// 1. Pon un selector estable en la zona que se quiere enseñar
//    (`data-guia="..."`, o reutiliza un `data-uso` o `data-testid` que ya exista).
// 2. Añade una entrada al final de NOVEDADES (la lista va por fecha):
//    - id: texto único y para siempre (nunca se reutiliza ni se cambia: es lo
//      que se guarda como visto).
//    - fecha: 'AAAA-MM-DD' de la entrega.
//    - titulo: nombre corto de la utilidad.
//    - aplica({ ruta, buscar }): true cuando se puede enseñar ahora mismo.
//      `ruta` es el pathname actual y `buscar(selector)` dice si hay un
//      elemento visible con ese selector. Que sea barato: se mira cada segundo
//      durante unos segundos tras cada cambio de pantalla.
//    - pasos: 1 o 2 tarjetas informativas { selector, titulo, texto, posicion? }
//      (posicion: 'auto' | 'arriba' | 'abajo'). Sin clics obligatorios ni
//      navegación. Si la zona de un paso que no es el primero no aparece en
//      2 s, la tarjeta se enseña igual, como texto sin resaltar.
// 3. Textos en español, sin emojis, cortos y en tono de "ahora puedes".
// 4. Si la zona solo aparece al abrir un modal o un panel (sin cambiar de
//    ruta), llama a revisarNovedades() al abrirlo: vuelve a abrir la ventana
//    de 15 s en la que se mira si alguna novedad aplica.

// Aviso a NovedadesPendientes para que vuelva a mirar ahora
export const EVENTO_REVISAR_NOVEDADES = 'duetto:revisar-novedades';
export const revisarNovedades = () => {
  try {
    window.dispatchEvent(new Event(EVENTO_REVISAR_NOVEDADES));
  } catch (e) {
    // Sin window (pruebas): nada que revisar
  }
};

export const NOVEDADES = [
  {
    id: 'lineas-arrastrar',
    fecha: '2026-10-15',
    titulo: 'Mover líneas arrastrando',
    // Ficha de presupuesto con el formato nuevo, en el Resumen y con líneas que se pueden mover
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/presupuestos/')
      && buscar('[data-uso="presupuesto.linea_arrastrar"]')
    ),
    pasos: [
      {
        selector: '[data-uso="presupuesto.linea_arrastrar"]',
        titulo: 'Ahora puedes arrastrar las líneas',
        texto: 'Agarra una línea por esta asa y suéltala donde quieras. Un solo movimiento, un solo guardado. Las flechas de subir y bajar siguen ahí.',
      },
      {
        selector: '[data-guia="linea-opciones"]',
        titulo: 'Mover al principio o al final',
        texto: 'En el menú de la línea tienes Subir, Bajar, Mover al principio y Mover al final. En el móvil, donde no se puede arrastrar, usa estas opciones.',
      },
    ],
  },
  {
    id: 'menu-un-clic',
    fecha: '2026-10-15',
    titulo: 'Añadir un menú en un clic',
    // Ficha de presupuesto con el modal "Añadir Menú al Presupuesto" abierto
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/presupuestos/')
      && buscar('[data-guia="menu-anadir-rapido"]')
    ),
    pasos: [
      {
        selector: '[data-guia="menu-anadir-rapido"]',
        titulo: 'Añadir en un clic',
        texto: 'Pulsa Añadir en el menú que quieras y entra con las personas del presupuesto y la bebida del catálogo. Si quieres cambiar algo antes, toca el nombre del menú como hasta ahora.',
      },
    ],
  },
  {
    id: 'calendario-hoy',
    fecha: '2026-10-15',
    titulo: 'Volver a hoy en el calendario',
    // Calendario de eventos, escritorio o móvil, con el botón Hoy en la cabecera
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/calendario-eventos')
      && buscar('[data-guia="calendario-hoy"]')
    ),
    pasos: [
      {
        selector: '[data-guia="calendario-hoy"]',
        titulo: 'Volver a hoy',
        texto: 'Si te has ido a otro mes, con Hoy vuelves al mes y al día de hoy en un toque.',
      },
    ],
  },
  {
    id: 'evento-siguiente',
    fecha: '2026-10-15',
    titulo: 'Pasar al siguiente evento',
    // Solo con la ventana de un evento abierta desde una lista (la abre
    // CalendarioEventos.js, que avisa con revisarNovedades())
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/calendario-eventos')
      && buscar('[data-guia="evento-siguiente"]')
    ),
    pasos: [
      {
        selector: '[data-guia="evento-siguiente"]',
        titulo: 'Los eventos del mismo día, seguidos',
        texto: 'Con Anterior y Siguiente pasas de un evento a otro del mismo día sin cerrar la ventana. En el ordenador también valen las flechas del teclado.',
      },
    ],
  },
  {
    id: 'inicio-segun-fichaje',
    fecha: '2026-10-15',
    titulo: 'Al entrar: Mi Inicio o el calendario',
    // Mi Inicio con sus pestañas a la vista (components/InicioSegunFichaje.js decide al entrar)
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/dashboard-empleado')
      && buscar('[data-tour="tab-hoy"]')
    ),
    pasos: [
      {
        selector: '[data-tour="tab-hoy"]',
        titulo: 'Primero fichar, luego el calendario',
        texto: 'Al entrar en el ERP verás Mi Inicio hasta que fiches. Una vez fichado, se abrirá directamente el calendario. Mi Inicio sigue en el menú cuando lo necesites.',
      },
    ],
  },
  {
    id: 'ficha-pantalla-inicio',
    fecha: '2026-10-15',
    titulo: 'Pantalla al entrar de cada empleado',
    // Ficha del empleado, pestaña Acceso, con el selector a la vista
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/empleados/')
      && buscar('[data-guia="pantalla-inicio"]')
    ),
    pasos: [
      {
        selector: '[data-guia="pantalla-inicio"]',
        titulo: 'Qué ve cada persona al entrar',
        texto: 'Aquí eliges si al entrar ve Mi Inicio hasta fichar y después el calendario, o siempre una de las dos.',
      },
    ],
  },
  {
    // Sustituye a 'ausencia-cita-medica' (retirada del catálogo el mismo día)
    id: 'ausencia-dia-ocupado',
    fecha: '2026-10-15',
    titulo: 'Días ocupados del equipo',
    // Gestión de equipo con el diálogo "Añadir ausencia" abierto (el selector solo
    // lleva data-guia para dirección; GestionEquipo llama a revisarNovedades() al abrirlo)
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/gestion-equipo')
      && buscar('[data-guia="ausencia-dia-ocupado"]')
    ),
    pasos: [
      {
        selector: '[data-guia="ausencia-dia-ocupado"]',
        titulo: 'Médico, cursos, trámites',
        texto: 'Elige Día ocupado y el motivo. Sale en amarillo en el calendario. Cada ausencia la ven solo la propia persona y dirección, nunca los compañeros. No descuenta vacaciones.',
      },
    ],
  },
  {
    id: 'ver-calendario',
    fecha: '2026-10-15',
    titulo: 'Ver el evento en el calendario',
    // Ficha de presupuesto con fecha (cabecera de los dos formatos)
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/presupuestos/')
      && buscar('[data-guia="ver-calendario"]')
    ),
    pasos: [
      {
        selector: '[data-guia="ver-calendario"]',
        titulo: 'Al calendario desde la ficha',
        texto: 'Con Ver en el calendario abres el día del evento con su ventana ya abierta y ves qué más hay ese día. Al cerrarla vuelves aquí con el botón Presupuesto.',
      },
    ],
  },
  {
    id: 'linea-horas',
    fecha: '2026-10-15',
    titulo: 'Horas de cada servicio en el Resumen',
    // Ficha de presupuesto (un día) en el Resumen, con alguna línea de servicio editable
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/presupuestos/')
      && buscar('[data-guia="linea-horas"]')
    ),
    pasos: [
      {
        selector: '[data-guia="linea-horas"]',
        titulo: 'La hora de cada servicio, aquí',
        texto: 'Pon aquí la hora de inicio y fin de cada servicio. Los complementos dicen a qué servicio acompañan. Si falta alguna, Proponer horas la rellena.',
      },
    ],
  },
  {
    id: 'multievento-horas',
    fecha: '2026-10-16',
    titulo: 'Horas del multievento sincronizadas',
    // Ficha de un multievento en el Resumen, con alguna línea de servicio editable
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/presupuestos/')
      && buscar('[data-guia="multievento-horas"]')
    ),
    pasos: [
      {
        selector: '[data-guia="multievento-horas"]',
        titulo: 'Una hora para todos los días',
        texto: 'La hora que pongas aquí se aplica al mismo servicio en todos los días. Debajo ves la de cada día; si un día tiene hora propia se respeta, salvo que pulses Igualar todas.',
      },
    ],
  },
  {
    id: 'horario-servicios-logistica',
    fecha: '2026-10-16',
    titulo: 'Horas de los servicios en Horario',
    // Ficha de presupuesto en Logística > Horario, con la tarjeta "Horas de los servicios"
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/presupuestos/')
      && buscar('[data-guia="horario-servicios"]')
    ),
    pasos: [
      {
        selector: '[data-guia="horario-servicios"]',
        titulo: 'Las horas de cada servicio, también aquí',
        texto: 'Desde Horario puedes ver y cambiar la hora de cada servicio. Es la misma que en el Resumen: se cambia en un sitio y se ve en el otro, y los tramos del responsable se recalculan solos.',
      },
    ],
  },
  {
    id: 'aviso-horarios-confirmar',
    fecha: '2026-10-16',
    titulo: 'Aviso de servicios sin hora',
    // Ventana del aviso al confirmar o al sacar la orden de cocina (ficha o calendario)
    aplica: ({ buscar }) => buscar('[data-testid="aviso-horarios-lista"]'),
    pasos: [
      {
        selector: '[data-testid="aviso-horarios-lista"]',
        titulo: 'Antes de confirmar, las horas',
        texto: 'Si a algún servicio le falta la hora, aquí ves cuál y qué día. Puedes ponerla ahora o seguir igualmente: no bloquea nada.',
      },
    ],
  },
  {
    id: 'iamail-borradores',
    fecha: '2026-10-16',
    titulo: 'Borradores del correo',
    // Correo con el menú de carpetas a la vista (entrada Borradores, pages/Emails.js)
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/emails')
      && buscar('[data-guia="iamail-borradores"]')
    ),
    pasos: [
      {
        selector: '[data-guia="iamail-borradores"]',
        titulo: 'Tus borradores, aquí',
        texto: 'Aquí ves los borradores guardados en tu correo, también los que hayas empezado en el móvil. Ábrelo para revisarlo, cambiarlo y enviarlo.',
      },
    ],
  },
  {
    id: 'iamail-ver-adjunto',
    fecha: '2026-10-17',
    titulo: 'Ver los adjuntos antes de enviar',
    // Editor de IAmail abierto con al menos un adjunto
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/emails')
      && buscar('[data-guia="adjunto-ver"]')
    ),
    pasos: [
      {
        selector: '[data-guia="adjunto-ver"]',
        titulo: 'Pulsa el adjunto para verlo',
        texto: 'Antes de enviar, pulsa el nombre de un adjunto (o la factura adjunta) y se abre para que veas exactamente lo que vas a mandar.',
      },
    ],
  },
  {
    id: 'iamail-movil-lectura',
    fecha: '2026-10-17',
    titulo: 'IAmail en el móvil',
    // Correo abierto en el móvil: barra inferior de acciones (pages/Emails.js, solo < 768 px)
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/emails')
      && buscar('[data-guia="iamail-barra-movil"]')
    ),
    pasos: [
      {
        selector: '[data-guia="iamail-barra-movil"]',
        titulo: 'Las acciones del correo, al alcance del pulgar',
        texto: 'Responder, Gestionado y Archivar están aquí abajo. En Más tienes Reenviar, Destacar, Vincular, Spam y Papelera. El botón atrás del móvil cierra el correo en vez de salir.',
        posicion: 'arriba',
      },
      {
        selector: '[data-guia="iamail-resumen-movil"]',
        titulo: 'Resumen de la IA en una línea',
        texto: 'Toca la línea para ver el análisis completo y las acciones sugeridas. Los adjuntos están arriba del todo: toca uno para verlo. Los enlaces a presupuestos y clientes se abren dentro de la app.',
      },
    ],
  },
  {
    id: 'iamail-movil-editor',
    fecha: '2026-10-17',
    titulo: 'Enviar siempre a la vista',
    // Editor abierto en el móvil, a pantalla completa con Enviar en la cabecera
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/emails')
      && buscar('[data-guia="iamail-enviar-movil"]')
    ),
    pasos: [
      {
        selector: '[data-guia="iamail-enviar-movil"]',
        titulo: 'Enviar siempre a la vista',
        texto: 'En el móvil el editor ocupa toda la pantalla y Enviar está arriba, también con el teclado abierto. Abajo tienes Adjuntar, la firma y Guardar borrador.',
        posicion: 'abajo',
      },
    ],
  },
  {
    id: 'alergenos-sustitutos-distintos',
    fecha: '2026-10-20',
    titulo: 'Sustitutos sin repetir',
    // Pestaña Alérgenos con la zona "Menú de cada grupo" y alguna propuesta
    // del ERP a la vista (PestanaAlergenos avisa con revisarNovedades al
    // aparecer la zona, también con la ficha antigua, que no cambia la URL)
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/presupuestos/')
      && buscar('[data-guia="alergenos-sustituciones"]')
      && buscar('[data-testid="alergenos-propuesta-erp"]')
    ),
    pasos: [
      {
        selector: '[data-guia="alergenos-sustituciones"]',
        titulo: 'Sustitutos sin repetir',
        texto: 'Para cada plato que el comensal no puede comer, el ERP propone un sustituto distinto. Donde pone Propuesto por el ERP puedes cambiarlo con Cambiar plato.',
      },
    ],
  },
  {
    id: 'quien-va-camareros',
    fecha: '2026-10-20',
    titulo: 'Camareros de cada servicio',
    // Logística > Quién va y camareros con algún servicio que lleva personal
    // (PersonalMenu avisa con revisarNovedades al aparecer: la subpestaña no
    // cambia la URL)
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/presupuestos/')
      && buscar('[data-guia="quien-va-camareros"]')
    ),
    pasos: [
      {
        selector: '[data-guia="quien-va-camareros"]',
        titulo: 'Los camareros de cada servicio',
        texto: 'Propuesto es lo que calcula el ERP por comensales. En Van pones los que irán de verdad con más y menos; se guarda solo.',
      },
      {
        selector: '[data-guia="quien-va-tandas"]',
        titulo: 'Unos a una hora y otros a otra',
        texto: 'Pulsa Horas distintas y añade una tanda por cada grupo: 2 camareros de 11:00 a 15:00 y 3 de 13:00 a 17:00. La solicitud al proveedor se rellena con esas tandas.',
      },
    ],
  },
  {
    id: 'factura-correos-administracion',
    fecha: '2026-10-21',
    titulo: 'Correos antes de facturar',
    // Modal de factura del presupuesto con correos "para administración" sin
    // gestionar (AvisoAdministracionFactura avisa con revisarNovedades al
    // aparecer el bloque: el modal no cambia la URL)
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/presupuestos/')
      && buscar('[data-guia="factura-correos-administracion"]')
    ),
    pasos: [
      {
        selector: '[data-guia="factura-correos-administracion"]',
        titulo: 'Correos antes de facturar',
        texto: 'Si el cliente o un compañero ha escrito algo para administración sobre este evento (otra razón social, retener la factura...), sale aquí antes de emitir. Ábrelo o márcalo gestionado.',
      },
    ],
  },
  {
    id: 'iamail-copia-para-admin',
    fecha: '2026-10-21',
    titulo: 'En copia, pero para ti',
    // Panel de IAmail con un correo en copia para administración en
    // "Nuevo para ti" (Emails.js avisa con revisarNovedades cuando llega)
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/emails')
      && buscar('[data-guia="iamail-copia-para-admin"]')
    ),
    pasos: [
      {
        selector: '[data-guia="iamail-copia-para-admin"]',
        titulo: 'En copia, pero para ti',
        texto: 'Los correos en los que vas en copia salen en Nuevo para ti cuando piden algo a administración, con lo que hay que hacer debajo.',
      },
    ],
  },
  {
    id: 'ficha-movil',
    fecha: '2026-10-22',
    titulo: 'La ficha del presupuesto en el móvil',
    // Ficha de presupuesto (formato nuevo) en el móvil, en el Resumen con alguna
    // línea: tarjeta por línea y barra inferior (solo < 768 px, PresupuestoDetalle.js)
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/presupuestos/')
      && buscar('[data-guia="ficha-movil-linea"]')
      && buscar('[data-guia="ficha-movil-barra"]')
    ),
    pasos: [
      {
        selector: '[data-guia="ficha-movil-linea"]',
        titulo: 'Toca una línea para cambiarla',
        texto: 'Se abre abajo con las personas, el precio, el IVA y la hora, con números grandes. El botón de los tres puntos tiene el resto: platos, dietas, descuento, mover y quitar.',
      },
      {
        selector: '[data-guia="ficha-movil-barra"]',
        titulo: 'Abajo: total, siguiente paso y Más',
        texto: 'El total siempre a la vista (tócalo para ver los costes), el siguiente paso (enviar, confirmar, facturar) y en Más: duplicar, guardar y el resto de acciones. Los documentos están en la cabecera.',
        posicion: 'arriba',
      },
    ],
  },
  {
    id: 'presupuestos-movil',
    fecha: '2026-10-22',
    titulo: 'La lista de presupuestos en el móvil',
    // Lista de presupuestos en el móvil: buscador con chips y botón Nuevo
    // flotante (solo existen con < 768 px, Presupuestos.js)
    aplica: ({ ruta, buscar }) => (
      String(ruta || '') === '/presupuestos'
      && buscar('[data-guia="presupuestos-movil-buscar"]')
      && buscar('[data-guia="presupuestos-movil-nuevo"]')
    ),
    pasos: [
      {
        selector: '[data-guia="presupuestos-movil-buscar"]',
        titulo: 'Busca y filtra con los chips',
        texto: 'Busca por número o cliente y filtra por estado con los chips. Toca una tarjeta para abrir el presupuesto.',
        posicion: 'abajo',
      },
      {
        selector: '[data-guia="presupuestos-movil-nuevo"]',
        titulo: 'Nuevo abajo a la derecha',
        texto: 'Crea un presupuesto con el botón Nuevo. El alta se abre a pantalla completa y el botón atrás la cierra.',
        posicion: 'arriba',
      },
    ],
  },
  {
    id: 'iamail-ayuda-ia',
    fecha: '2026-10-23',
    titulo: 'Ayuda IA en cada correo',
    // Correo abierto con el botón Ayuda IA en la cabecera (solo escritorio y
    // solo para quien la tiene activada; Emails.js avisa con revisarNovedades
    // al saber que está activa con un correo abierto: abrirlo no cambia la ruta)
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/emails')
      && buscar('[data-guia="iamail-ayuda-ia"]')
    ),
    pasos: [
      {
        selector: '[data-guia="iamail-ayuda-ia"]',
        titulo: 'Ayuda IA en cada correo',
        texto: 'Pídele lo que haría falta con este correo: crear el cliente, cambiar a quién se factura, preparar la respuesta. Te propone los pasos y no hace nada sin tu OK.',
      },
      {
        selector: '[data-guia="iamail-ayuda-ia"]',
        titulo: 'Nunca envía ni factura sola',
        texto: 'Las respuestas quedan en Borradores para que las revises, y emitir o regenerar una factura te pide una segunda confirmación.',
      },
    ],
  },
  {
    id: 'iamail-ayuda-ia-personal',
    fecha: '2026-10-27',
    titulo: 'Ayuda IA: altas y bajas de empleados',
    // Mismo botón que iamail-ayuda-ia (solo quien tiene Ayuda IA activada)
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/emails')
      && buscar('[data-guia="iamail-ayuda-ia"]')
    ),
    pasos: [
      {
        selector: '[data-guia="iamail-ayuda-ia"]',
        titulo: 'Altas y bajas desde el correo de la gestoría',
        texto: 'Con el contrato o la carta de baja en el correo, pídele "da de alta a este empleado" o "dale de baja". Lee los documentos, crea la ficha, guarda los papeles, asigna las firmas e invita al portal si hay email.',
      },
      {
        selector: '[data-guia="iamail-ayuda-ia"]',
        titulo: 'La baja pide confirmación',
        texto: 'Dar de baja a alguien te pide una segunda confirmación con su nombre y la fecha, como las facturas.',
      },
    ],
  },
  {
    id: 'ajustes-clave-claude',
    fecha: '2026-10-23',
    titulo: 'Clave de Claude',
    // Ajustes > Conexiones (solo dirección): la tarjeta solo existe en esa
    // sección y cambiar de sección no cambia la ruta, así que Ajustes.js avisa
    // con revisarNovedades() al pintarla
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/ajustes')
      && buscar('[data-guia="ajustes-clave-claude"]')
    ),
    pasos: [
      {
        selector: '[data-guia="ajustes-clave-claude"]',
        titulo: 'Clave de Claude',
        texto: 'Pega aquí la clave de la API de Claude para activar Ayuda IA en IAmail. Se guarda cifrada y puedes probarla o quitarla cuando quieras.',
      },
    ],
  },
  {
    id: 'finanzas-pago-nominas',
    fecha: '2026-10-30',
    titulo: 'Pago de nóminas en Finanzas',
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/finanzas') && buscar('[data-testid="tab-nominas"]')
    ),
    pasos: [
      {
        selector: '[data-testid="tab-nominas"]',
        titulo: 'Pago de nóminas',
        texto: 'Ahora tienes aquí la remesa de nóminas de cada mes para el BBVA, con su total y si ya está pagada.',
      },
      {
        selector: '[data-testid="tab-nominas"]',
        titulo: 'Descarga y paga',
        texto: 'Entra en Nóminas y pulsa "Descargar Excel BBVA" en el mes que toque. Ese fichero es el que se sube a la banca.',
      },
    ],
  },
  {
    id: 'factura-numero-pedido',
    fecha: '2026-11-03',
    titulo: 'Nº de pedido del cliente en la factura',
    // Modal de factura de la ficha de presupuesto: no cambia la ruta, la
    // ficha avisa con revisarNovedades() al abrirlo
    aplica: ({ ruta, buscar }) => (
      String(ruta || '').startsWith('/presupuestos/')
      && buscar('[data-guia="factura-numero-pedido"]')
    ),
    pasos: [
      {
        selector: '[data-guia="factura-numero-pedido"]',
        titulo: 'Nº de pedido del cliente',
        texto: 'Si el cliente te manda un pedido u orden de compra, escribe aquí su número. Sale en la cabecera de la factura, junto al presupuesto, y se conserva al regenerarla. Antes solo se podía poner en Observaciones.',
      },
    ],
  },
];

export default NOVEDADES;
